<template>
  <DetailsAccordion
    :error-description="i18n(translations.errorDescription)"
    :status
    @refresh="refresh"
  >
    <template #icon>
      <IconSmartphone />
    </template>
    <template #title>
      {{ i18n(translations.title) }}
    </template>
    <template #description>
      {{ product?.title }}
    </template>
    <template #body>
      <div class="text-static-default-low">
        <div class="mb-16">
          <p v-if="orderline">
            {{ i18n(translations.quantity, { quantity: orderline.quantity }) }}
          </p>
          <p>{{ i18n(translations.price, { price }) }}</p>
          <p v-if="product?.sku">
            <FormattedMessage :definition="translations.sku">
              <template #productLink>
                <RevLink target="_blank" :to="productLink">
                  <span class="text-static-default-low body-1-bold">
                    {{ product?.sku }}
                  </span>
                </RevLink>
              </template>
            </FormattedMessage>
          </p>
        </div>

        <p>
          {{ i18n(translations.grade, { grade: product?.backboxGrade.name }) }}
        </p>
        <p v-if="product?.isNewBatteryDevice">
          {{ i18n(translations.newBatteryDevice) }}
        </p>
        <p v-if="serialNumber">
          {{ i18n(translations.serialNumber, { serialNumber }) }}
        </p>
        <p v-if="imei">
          {{ i18n(translations.imei, { imei }) }}
        </p>
        <p v-if="product?.isYakuzaPhone">
          {{ i18n(translations.yakuzaPhone) }}
        </p>
        <p v-if="product?.comment">{{ product.comment }}</p>
      </div>
    </template>
  </DetailsAccordion>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevLink } from '@ds/components/Link'
import { IconSmartphone } from '@ds/icons/IconSmartphone'

import { ROUTE_NAMES } from '~/constants/routes'
import { useOrderlineDetails } from '~/scopes/customer-care/pages/request/composables/useOrderlineDetails'

import DetailsAccordion from '../DetailsAccordion/DetailsAccordion.vue'

import translations from './ProductDetails.translations'

const i18n = useI18n()
const locale = useI18nLocale()

const {
  productDetails: { data, status, refresh },
} = useOrderlineDetails()

const { orderline, product } = data

const price = computed(() => {
  if (!orderline.value) return ''

  const shipping =
    parseInt(orderline.value.shippingPrice, 10) > 0
      ? ` + ${i18n(translations.shippingPrice, { shippingPrice: orderline.value.shippingPrice })}`
      : ''

  const tax =
    parseInt(orderline.value.salesTaxPrice, 10) > 0
      ? ` + ${i18n(translations.salesTaxPrice, { salesTaxPrice: orderline.value.salesTaxPrice })}`
      : ''

  return `${orderline.value.price}${shipping}${tax}`
})

const productLink = computed(() => {
  if (product.value && product.value.akeneoId && product.value.productSlug) {
    return {
      name: ROUTE_NAMES.FRONT_OFFICE.PRODUCT.HOME,
      params: {
        locale,
        slugV2: product.value.productSlug,
        uuid: product.value.akeneoId,
      },
    }
  }

  return ''
})

const imei = computed(() => {
  if (isEmpty(orderline.value?.itemIdentifier)) return null
  const { type, value } = orderline.value.itemIdentifier

  return type === 'IMEI' ? value : null
})

const serialNumber = computed(() => {
  if (isEmpty(orderline.value?.itemIdentifier)) return null
  const { type, value } = orderline.value.itemIdentifier

  return type === 'SERIAL_NUMBER' ? value : null
})
</script>
